import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { HandCoins, Plus, ChevronDown, ChevronUp, Banknote, ArrowDownLeft, ArrowUpRight, FileDown, Trash2, AlertCircle, Calendar, Clock, FileText, XCircle } from 'lucide-react';
import CurrencySymbol from '@/components/CurrencySymbol';
import { exportListPDF } from '@/utils/pdfExport';
import { fmtNum } from '@/utils/currency';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function Loans() {
  const { token } = useContext(AuthContext);
  const [tab, setTab] = useState('loan');
  const [loans, setLoans] = useState([]);
  const [banks, setBanks] = useState([]);
  const [expandedLoan, setExpandedLoan] = useState(null);
  const [loanDetail, setLoanDetail] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showWriteOffDialog, setShowWriteOffDialog] = useState(false);
  const [writeOffForm, setWriteOffForm] = useState({ amount: '', note: '' });

  const [form, setForm] = useState({
    type: 'loan', direction: 'to', amount: '', currency: 'USD',
    date: new Date().toISOString().split('T')[0], due_date: '',
    purpose: '', person: '', repayment_condition: '',
    repayment_structure: 'full_payment', reminder_days: '7',
    bank_id: '', account_number: '', exchange_rate: ''
  });

  const [payForm, setPayForm] = useState({
    amount: '', bank_id: '', account_number: '', exchange_rate: '', note: ''
  });

  useEffect(() => { fetchLoans(); fetchBanks(); }, [tab]);

  // Handle highlight from URL query param (e.g., /loans?highlight=loanId)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const highlightId = params.get('highlight');
    if (highlightId && loans.length > 0) {
      const inCurrentTab = loans.find(l => l.id === highlightId);
      if (inCurrentTab) {
        // Fetch detail and expand the loan card
        fetchDetail(highlightId);
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="loan-card-${highlightId}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      } else if (tab === 'loan') {
        // Try the other tab
        setTab('borrowing');
      }
    }
  }, [loans, tab]);

  const fetchLoans = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/loans?type=${tab}`, { headers: { Authorization: `Bearer ${token}` } });
      setLoans(r.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const fetchBanks = async () => {
    try {
      const r = await axios.get(`${API}/accounting/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setBanks(r.data.banks || []);
    } catch {}
  };

  const fetchDetail = async (loanId) => {
    if (expandedLoan === loanId) { setExpandedLoan(null); setLoanDetail(null); return; }
    try {
      const r = await axios.get(`${API}/loans/${loanId}`, { headers: { Authorization: `Bearer ${token}` } });
      setLoanDetail(r.data);
      setExpandedLoan(loanId);
    } catch { toast.error('Failed to load details'); }
  };

  const handleCreate = async () => {
    if (!form.amount || !form.person) { toast.error('Amount and person are required'); return; }
    if (!form.bank_id || !form.account_number) { toast.error('Please select a bank account'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form, amount: parseFloat(form.amount), type: tab,
        date: new Date(form.date).toISOString(),
        reminder_days: parseInt(form.reminder_days) || 7
      };
      if (form.exchange_rate) payload.exchange_rate = parseFloat(form.exchange_rate);
      await axios.post(`${API}/loans`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${tab === 'loan' ? 'Loan' : 'Borrowing'} created`);
      setShowCreateDialog(false);
      resetForm();
      fetchLoans();
      fetchBanks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSubmitting(false); }
  };

  const handlePayment = async () => {
    if (!payForm.amount || !payForm.bank_id || !payForm.account_number) {
      toast.error('Amount and bank account are required'); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        amount: parseFloat(payForm.amount),
        bank_id: payForm.bank_id,
        account_number: payForm.account_number,
        note: payForm.note
      };
      if (payForm.exchange_rate) payload.exchange_rate = parseFloat(payForm.exchange_rate);
      const r = await axios.post(`${API}/loans/${selectedLoan.id}/payment`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message);

      // Auto-generate receipt for the payment just made
      if (r.data.payment) {
        generatePaymentReceipt(r.data.payment, selectedLoan);
      }

      setShowPaymentDialog(false);
      setPayForm({ amount: '', bank_id: '', account_number: '', exchange_rate: '', note: '' });
      fetchLoans(); fetchBanks();
      if (expandedLoan === selectedLoan.id) {
        setExpandedLoan(null);
        setTimeout(() => fetchDetail(selectedLoan.id), 300);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API}/loans/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      fetchLoans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const handleWriteOff = async () => {
    if (!writeOffForm.amount || !selectedLoan) { toast.error('Amount is required'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/loans/${selectedLoan.id}/write-off`, {
        amount: parseFloat(writeOffForm.amount),
        note: writeOffForm.note
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message);
      setShowWriteOffDialog(false);
      setWriteOffForm({ amount: '', note: '' });
      fetchLoans();
      if (expandedLoan === selectedLoan.id) {
        setTimeout(() => fetchDetail(selectedLoan.id), 300);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Write-off failed'); }
    finally { setSubmitting(false); }
  };

  const resetForm = () => setForm({
    type: tab, direction: tab === 'loan' ? 'to' : 'from', amount: '', currency: 'USD',
    date: new Date().toISOString().split('T')[0], due_date: '',
    purpose: '', person: '', repayment_condition: '',
    repayment_structure: 'full_payment', reminder_days: '7',
    bank_id: '', account_number: '', exchange_rate: ''
  });

  const handleExportPDF = () => {
    const rows = loans.map((l, i) => [
      i + 1, new Date(l.date).toLocaleDateString(), l.person || '-',
      l.direction === 'to' ? 'Lent To' : 'Received From',
      `${l.currency} ${fmtNum(l.amount)}`,
      `${l.currency} ${fmtNum(l.paid_amount)}`,
      `${l.currency} ${fmtNum(l.remaining)}`,
      l.status === 'paid' ? 'Paid' : 'Active',
    ]);
    const totalRemaining = loans.reduce((s, l) => s + l.remaining, 0);
    exportListPDF({
      title: tab === 'loan' ? 'Loans' : 'Borrowings',
      headers: ['#', 'Date', 'Person', 'Direction', 'Amount', 'Paid', 'Balance', 'Status'],
      rows, totalLabel: 'Total Outstanding', totalValue: `$${fmtNum(totalRemaining)}`,
      token, apiUrl: API,
    });
  };

  const selectedPayBank = banks.find(b => b.id === payForm.bank_id);
  const selectedPayAccount = selectedPayBank?.accounts?.find(a => a.account_number === payForm.account_number);
  const needsExchangeRate = selectedLoan && selectedPayAccount && selectedPayAccount.currency !== selectedLoan.currency;

  const selectedCreateBank = banks.find(b => b.id === form.bank_id);
  const selectedCreateAccount = selectedCreateBank?.accounts?.find(a => a.account_number === form.account_number);
  const createNeedsExchangeRate = selectedCreateAccount && selectedCreateAccount.currency !== form.currency;

  const activeLoans = loans.filter(l => l.status === 'active');
  const paidLoans = loans.filter(l => l.status === 'paid');
  const overdueLoans = activeLoans.filter(l => l.due_date && new Date(l.due_date) < new Date());
  const dueSoonLoans = activeLoans.filter(l => {
    if (!l.due_date) return false;
    const due = new Date(l.due_date);
    const now = new Date();
    if (due < now) return false; // already overdue
    const reminder = l.reminder_days || 7;
    const threshold = new Date();
    threshold.setDate(threshold.getDate() + reminder);
    return due <= threshold;
  });
  const totalOutstanding = activeLoans.reduce((s, l) => s + l.remaining, 0);
  const totalAmount = loans.reduce((s, l) => s + l.amount, 0);

  // Per-currency breakdowns for summary cards
  const currencyBreakdown = (items, field) => {
    const breakdown = {};
    items.forEach(l => {
      const c = l.currency || 'USD';
      if (!breakdown[c]) breakdown[c] = 0;
      breakdown[c] += l[field];
    });
    return Object.entries(breakdown).sort((a, b) => b[1] - a[1]);
  };
  const totalBreakdown = currencyBreakdown(loans, 'amount');
  const outstandingBreakdown = currencyBreakdown(activeLoans, 'remaining');

  const generateLoanStatement = async (loan) => {
    let logo = null;
    try {
      const res = await axios.get(`${API}/settings/company`, { headers: { Authorization: `Bearer ${token}` } });
      logo = res.data?.company_logo;
    } catch {}
    let payments = [];
    try {
      const r = await axios.get(`${API}/loans/${loan.id}`, { headers: { Authorization: `Bearer ${token}` } });
      payments = r.data.payments || [];
    } catch {}

    const doc = new jsPDF();
    let y = 15;

    // Header
    if (logo) {
      try { doc.addImage(logo, 'AUTO', 14, 10, 22, 22); } catch {}
      doc.setFontSize(16); doc.setTextColor(30, 58, 138);
      doc.text('Loan Statement', 42, 22);
      doc.setFontSize(9); doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 42, 28);
      y = 40;
    } else {
      doc.setFontSize(16); doc.setTextColor(30, 58, 138);
      doc.text('Loan Statement', 14, 20);
      doc.setFontSize(9); doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 27);
      y = 34;
    }

    // Loan details
    doc.setFontSize(11); doc.setTextColor(30, 41, 59);
    doc.text(`${loan.type === 'loan' ? 'Lent To' : 'Borrowed From'}: ${loan.person}`, 14, y);
    y += 7;
    const detailRows = [
      ['Amount', `${loan.currency} ${fmtNum(loan.amount)}`],
      ['Paid', `${loan.currency} ${fmtNum(loan.paid_amount)}`],
      ['Outstanding', `${loan.currency} ${fmtNum(loan.remaining)}`],
      ['Status', loan.status === 'paid' ? 'Fully Paid' : 'Active'],
      ['Structure', (loan.repayment_structure || 'full_payment').replace('_', ' ')],
      ['Date Issued', new Date(loan.date).toLocaleDateString()],
      ['Due Date', loan.due_date ? new Date(loan.due_date).toLocaleDateString() : 'N/A'],
      ['Reminder', `${loan.reminder_days || 7} days before due`],
    ];
    if (loan.bank_id) {
      const linkedBank = banks.find(b => b.id === loan.bank_id);
      const bankLabel = linkedBank ? `${linkedBank.bank_name} — ${loan.account_number}` : loan.account_number;
      detailRows.push(['Bank Account', bankLabel]);
      detailRows.push(['Transaction Ref', `LC-${loan.id.slice(0, 8).toUpperCase()}`]);
    }
    if (loan.purpose) detailRows.push(['Purpose', loan.purpose]);
    if (loan.repayment_condition) detailRows.push(['Condition', loan.repayment_condition]);

    autoTable(doc, {
      startY: y, body: detailRows, theme: 'plain',
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45 } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 10;

    // Payment history
    doc.setFontSize(12); doc.setTextColor(30, 58, 138);
    doc.text('Payment History', 14, y);
    y += 5;

    if (payments.length === 0) {
      doc.setFontSize(9); doc.setTextColor(120);
      doc.text('No payments recorded yet.', 14, y + 4);
    } else {
      let runningBalance = loan.amount;
      const paymentRows = payments.slice().reverse().map((p) => {
        runningBalance -= p.amount;
        return [
          new Date(p.created_at).toLocaleDateString(),
          `${loan.currency} ${fmtNum(p.amount)}`,
          p.debit_currency !== loan.currency ? `${p.debit_currency} ${fmtNum(p.debit_amount)}` : '-',
          `${p.bank_name} (${p.account_number})`,
          `${loan.currency} ${fmtNum(Math.max(runningBalance, 0))}`,
          p.note || '-',
        ];
      });

      autoTable(doc, {
        startY: y,
        head: [['Date', 'Amount', 'Converted', 'Bank Account', 'Balance After', 'Note']],
        body: paymentRows, theme: 'striped',
        styles: { fontSize: 8, cellPadding: 2.5 },
        headStyles: { fillColor: [30, 58, 138], textColor: 255, fontSize: 8 },
        margin: { left: 14, right: 14 },
      });
    }

    doc.save(`Loan_Statement_${loan.person.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Statement PDF downloaded');
  };

  const generatePaymentReceipt = async (payment, loan) => {
    let logo = null;
    let companyName = 'ASSETCO';
    try {
      const res = await axios.get(`${API}/settings/company`, { headers: { Authorization: `Bearer ${token}` } });
      logo = res.data?.company_logo;
      companyName = res.data?.company_name || 'ASSETCO';
    } catch {}

    const doc = new jsPDF();
    let y = 15;

    // Header with logo
    if (logo) {
      try { doc.addImage(logo, 'AUTO', 14, 10, 22, 22); } catch {}
      doc.setFontSize(18); doc.setTextColor(30, 58, 138);
      doc.text('Payment Receipt', 42, 22);
      doc.setFontSize(9); doc.setTextColor(100);
      doc.text(companyName, 42, 28);
      y = 40;
    } else {
      doc.setFontSize(18); doc.setTextColor(30, 58, 138);
      doc.text('Payment Receipt', 14, 20);
      doc.setFontSize(9); doc.setTextColor(100);
      doc.text(companyName, 14, 27);
      y = 34;
    }

    // Receipt number & date
    doc.setFontSize(10); doc.setTextColor(30, 41, 59);
    const receiptNo = payment.receipt_number || `RCP-${payment.id.slice(0, 8).toUpperCase()}`;
    doc.text(`Receipt No: ${receiptNo}`, 14, y);
    doc.text(`Date: ${new Date(payment.created_at).toLocaleDateString()}`, 130, y);
    y += 10;

    // Separator
    doc.setDrawColor(200); doc.setLineWidth(0.3);
    doc.line(14, y, 196, y);
    y += 8;

    // Loan & party info
    const isLoan = loan.type === 'loan';
    const detailRows = [
      [isLoan ? 'Paid By' : 'Received From', loan.person],
      ['Loan Type', isLoan ? 'Loan (Lent To)' : 'Borrowing (Received From)'],
      ['Purpose', loan.purpose || '-'],
      ['Original Amount', `${loan.currency} ${fmtNum(loan.amount)}`],
    ];

    autoTable(doc, {
      startY: y, body: detailRows, theme: 'plain',
      styles: { fontSize: 9, cellPadding: 2.5 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Payment details box
    doc.setFillColor(240, 249, 255);
    doc.roundedRect(14, y, 182, 50, 3, 3, 'F');

    doc.setFontSize(11); doc.setTextColor(30, 58, 138);
    doc.text('Payment Details', 22, y + 10);

    doc.setFontSize(10); doc.setTextColor(50);
    doc.text(`Amount Paid:`, 22, y + 20);
    doc.setFontSize(14); doc.setTextColor(16, 185, 129);
    doc.text(`${payment.loan_currency} ${fmtNum(payment.amount)}`, 65, y + 20);

    doc.setFontSize(9); doc.setTextColor(80);
    if (payment.debit_currency !== payment.loan_currency) {
      doc.text(`Debited: ${payment.debit_currency} ${fmtNum(payment.debit_amount)}  (Rate: ${payment.exchange_rate || 'N/A'})`, 22, y + 28);
    }
    doc.text(`Bank: ${payment.bank_name}  |  Account: ${payment.account_number}`, 22, y + 36);
    doc.text(`Remaining Balance: ${loan.currency} ${fmtNum(payment.remaining_after ?? loan.remaining)}`, 22, y + 44);
    y += 60;

    // Paid by
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text(`Processed by: ${payment.paid_by_name || payment.paid_by}`, 14, y);
    if (payment.note) {
      doc.text(`Note: ${payment.note}`, 14, y + 6);
      y += 6;
    }
    y += 16;

    // Signature lines
    doc.setDrawColor(180); doc.setLineWidth(0.2);
    doc.line(14, y + 10, 80, y + 10);
    doc.line(120, y + 10, 196, y + 10);
    doc.setFontSize(8); doc.setTextColor(130);
    doc.text('Authorized Signature', 28, y + 16);
    doc.text('Recipient Signature', 138, y + 16);

    doc.save(`Receipt_${receiptNo}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Payment receipt downloaded');
  };

  const buildRepaymentSchedule = (loan) => {
    if (loan.repayment_structure !== 'monthly' || !loan.date || !loan.due_date) return [];
    const start = new Date(loan.date);
    const end = new Date(loan.due_date);
    const months = [];
    const cur = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    while (cur <= end) {
      months.push(new Date(cur));
      cur.setMonth(cur.getMonth() + 1);
    }
    if (months.length === 0) return [];
    const monthlyAmt = loan.amount / months.length;
    const now = new Date();
    let cumulativeDue = 0;
    return months.map((m, i) => {
      cumulativeDue += monthlyAmt;
      const paidSoFar = Math.min(loan.paid_amount, cumulativeDue);
      const dueThisMonth = monthlyAmt;
      const paidThisMonth = Math.max(0, Math.min(monthlyAmt, paidSoFar - (i * monthlyAmt)));
      const isPast = m < now;
      const isCurrent = m.getMonth() === now.getMonth() && m.getFullYear() === now.getFullYear();
      return {
        date: m, dueAmount: dueThisMonth, paidAmount: paidThisMonth,
        status: paidThisMonth >= dueThisMonth ? 'paid' : isPast ? 'overdue' : isCurrent ? 'current' : 'upcoming',
      };
    });
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="loans-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Loans & Borrowings</h1>
            <p className="text-slate-600 mt-1">Track loans, borrowings and repayments</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportPDF} data-testid="loans-export-pdf">
              <FileDown className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} data-testid="create-loan-btn">
              <Plus className="w-4 h-4 mr-2" /> New {tab === 'loan' ? 'Loan' : 'Borrowing'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="loan-tabs">
          {[
            { key: 'loan', label: 'Loans', icon: ArrowUpRight },
            { key: 'borrowing', label: 'Borrowings', icon: ArrowDownLeft },
          ].map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setExpandedLoan(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`} data-testid={`tab-${t.key}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-4 bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <p className="text-blue-100 text-sm">Total {tab === 'loan' ? 'Loans' : 'Borrowings'}</p>
            <div className="mt-1 space-y-0.5">
              {totalBreakdown.map(([cur, amt]) => (
                <p key={cur} className="text-xl font-bold whitespace-nowrap leading-tight"><CurrencySymbol currency={cur} />{amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              ))}
              {totalBreakdown.length === 0 && <p className="text-xl font-bold">0.00</p>}
            </div>
            <p className="text-blue-200 text-xs mt-1">{loans.length} entries</p>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-amber-500 to-orange-600 text-white">
            <p className="text-amber-100 text-sm">Outstanding Balance</p>
            <div className="mt-1 space-y-0.5">
              {outstandingBreakdown.map(([cur, amt]) => (
                <p key={cur} className="text-xl font-bold whitespace-nowrap leading-tight"><CurrencySymbol currency={cur} />{amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              ))}
              {outstandingBreakdown.length === 0 && <p className="text-xl font-bold">0.00</p>}
            </div>
            <p className="text-amber-200 text-xs mt-1">{activeLoans.length} active</p>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <p className="text-emerald-100 text-sm">Fully Paid</p>
            <p className="text-2xl font-bold mt-1">{paidLoans.length}</p>
            <p className="text-emerald-200 text-xs mt-1">of {loans.length} total</p>
          </Card>
        </div>

        {/* Overdue Alert Banner */}
        {overdueLoans.length > 0 && (
          <Card className="p-4 border-red-300 bg-red-50" data-testid="overdue-alert-banner">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-800">
                  {overdueLoans.length} Overdue {overdueLoans.length === 1 ? 'Entry' : 'Entries'}
                </h3>
                <div className="mt-1 space-y-1">
                  {overdueLoans.map(l => (
                    <p key={l.id} className="text-xs text-red-700">
                      <span className="font-medium">{l.person}</span> — <CurrencySymbol currency={l.currency} />{fmtNum(l.remaining)} remaining, due {new Date(l.due_date).toLocaleDateString()}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Due Soon Alert Banner */}
        {dueSoonLoans.length > 0 && (
          <Card className="p-4 border-amber-300 bg-amber-50" data-testid="due-soon-alert-banner">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-800">
                  {dueSoonLoans.length} Due Soon
                </h3>
                <div className="mt-1 space-y-1">
                  {dueSoonLoans.map(l => (
                    <p key={l.id} className="text-xs text-amber-700">
                      <span className="font-medium">{l.person}</span> — <CurrencySymbol currency={l.currency} />{fmtNum(l.remaining)} remaining, due {new Date(l.due_date).toLocaleDateString()}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Loan List */}
        {loading ? (
          <p className="text-slate-500 text-center py-8">Loading...</p>
        ) : loans.length === 0 ? (
          <Card className="p-12 text-center">
            <HandCoins className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">No {tab === 'loan' ? 'loans' : 'borrowings'} recorded yet</p>
            <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Create First Entry
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {loans.map(loan => {
              const isExpanded = expandedLoan === loan.id;
              const pct = loan.amount > 0 ? ((loan.paid_amount / loan.amount) * 100) : 0;
              const isOverdue = loan.due_date && new Date(loan.due_date) < new Date() && loan.status === 'active';
              return (
                <Card key={loan.id} className={`overflow-hidden ${isOverdue ? 'border-red-300 bg-red-50/30' : ''}`} data-testid={`loan-card-${loan.id}`}>
                  <div className="p-4 cursor-pointer" onClick={() => fetchDetail(loan.id)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                            loan.direction === 'to' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {loan.direction === 'to' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}
                            {loan.direction === 'to' ? 'Lent To' : 'Received From'}
                          </span>
                          <span className="font-semibold text-slate-900">{loan.person || 'Unknown'}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            loan.status === 'paid' ? 'bg-green-100 text-green-700' : isOverdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {loan.status === 'paid' ? 'Paid' : isOverdue ? 'Overdue' : 'Active'}
                          </span>
                          {loan.repayment_structure === 'monthly' && (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">Monthly</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">{loan.purpose || 'No purpose specified'}</p>
                        <div className="flex gap-4 mt-2 text-xs text-slate-500">
                          <span>Date: {new Date(loan.date).toLocaleDateString()}</span>
                          {loan.due_date && <span className={isOverdue ? 'text-red-600 font-semibold' : ''}>Due: {new Date(loan.due_date).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-slate-900">
                          <CurrencySymbol currency={loan.currency} />{fmtNum(loan.amount)}
                        </p>
                        <p className={`text-sm font-medium ${loan.remaining > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          Balance: <CurrencySymbol currency={loan.currency} />{fmtNum(loan.remaining)}
                        </p>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400 ml-auto mt-1" /> : <ChevronDown className="w-4 h-4 text-slate-400 ml-auto mt-1" />}
                      </div>
                    </div>
                    <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
                        style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{pct.toFixed(0)}% repaid</p>
                    {loan.status === 'active' && (
                      <div className="mt-2 flex justify-end">
                        <Button size="sm" variant="outline"
                          className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          data-testid={`quick-pay-${loan.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLoan(loan);
                            setPayForm({ amount: '', bank_id: '', account_number: '', exchange_rate: '', note: '' });
                            setShowPaymentDialog(true);
                          }}>
                          <Banknote className="w-4 h-4 mr-1" /> Pay
                        </Button>
                      </div>
                    )}
                  </div>

                  {isExpanded && loanDetail && (
                    <div className="border-t bg-slate-50 p-4">
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                        <div><p className="text-xs text-slate-500">Repayment Condition</p><p className="text-sm font-medium">{loanDetail.loan.repayment_condition || '-'}</p></div>
                        <div><p className="text-xs text-slate-500">Structure</p><p className="text-sm font-medium capitalize">{loanDetail.loan.repayment_structure?.replace('_', ' ')}</p></div>
                        <div><p className="text-xs text-slate-500">Total Paid</p><p className="text-sm font-medium text-green-600"><CurrencySymbol currency={loan.currency} />{fmtNum(loanDetail.loan.paid_amount)}</p></div>
                        <div><p className="text-xs text-slate-500">Reminder</p><p className="text-sm font-medium">{loanDetail.loan.reminder_days || 7} days before due</p></div>
                        <div><p className="text-xs text-slate-500">Created By</p><p className="text-sm font-medium">{loanDetail.loan.created_by}</p></div>
                      </div>

                      <div className="flex gap-2 mb-4 flex-wrap">
                        {loan.status === 'active' && (
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); setSelectedLoan(loan); setPayForm({ amount: '', bank_id: '', account_number: '', exchange_rate: '', note: '' }); setShowPaymentDialog(true); }}
                            data-testid={`make-payment-${loan.id}`}>
                            <Banknote className="w-4 h-4 mr-1" /> Make Payment
                          </Button>
                        )}
                        {loan.status === 'active' && (
                          <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50"
                            data-testid={`write-off-${loan.id}`}
                            onClick={(e) => { e.stopPropagation(); setSelectedLoan(loan); setWriteOffForm({ amount: '', note: '' }); setShowWriteOffDialog(true); }}>
                            <XCircle className="w-4 h-4 mr-1" /> Write Off
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); generateLoanStatement(loan); }}
                          data-testid={`statement-pdf-${loan.id}`}>
                          <FileText className="w-4 h-4 mr-1" /> Statement PDF
                        </Button>
                        {loan.paid_amount === 0 && (
                          <Button size="sm" variant="outline" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleDelete(loan.id); }}>
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                          </Button>
                        )}
                      </div>

                      {/* Repayment Schedule for Monthly loans */}
                      {loanDetail.loan.repayment_structure === 'monthly' && (() => {
                        const schedule = buildRepaymentSchedule(loanDetail.loan);
                        if (schedule.length === 0) return null;
                        return (
                          <div className="mb-4" data-testid={`repayment-schedule-${loan.id}`}>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                              <Calendar className="w-4 h-4" /> Repayment Schedule
                            </h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                              {schedule.map((m, i) => (
                                <div key={i} className={`p-2 rounded border text-center text-xs ${
                                  m.status === 'paid' ? 'bg-green-50 border-green-300' :
                                  m.status === 'overdue' ? 'bg-red-50 border-red-300' :
                                  m.status === 'current' ? 'bg-blue-50 border-blue-300' :
                                  'bg-white border-slate-200'
                                }`}>
                                  <p className="font-semibold text-slate-700">
                                    {m.date.toLocaleString('default', { month: 'short', year: 'numeric' })}
                                  </p>
                                  <p className={`font-bold mt-0.5 ${
                                    m.status === 'paid' ? 'text-green-700' :
                                    m.status === 'overdue' ? 'text-red-700' :
                                    m.status === 'current' ? 'text-blue-700' : 'text-slate-600'
                                  }`}>
                                    <CurrencySymbol currency={loan.currency} />{fmtNum(m.dueAmount)}
                                  </p>
                                  <p className={`text-[10px] mt-0.5 font-medium ${
                                    m.status === 'paid' ? 'text-green-600' :
                                    m.status === 'overdue' ? 'text-red-600' :
                                    m.status === 'current' ? 'text-blue-600' : 'text-slate-400'
                                  }`}>
                                    {m.status === 'paid' ? 'Paid' : m.status === 'overdue' ? 'Overdue' : m.status === 'current' ? 'Due Now' : 'Upcoming'}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Payment History</h4>
                      {loanDetail.payments.length === 0 ? (
                        <p className="text-xs text-slate-400">No payments yet</p>
                      ) : (
                        <div className="space-y-2">
                          {loanDetail.payments.map(p => (
                            <div key={p.id} className="flex justify-between items-center bg-white rounded p-3 border text-sm">
                              <div>
                                <p className="font-medium">{new Date(p.created_at).toLocaleDateString()} - {p.bank_name}</p>
                                <p className="text-xs text-slate-500">
                                  {p.receipt_number && <span className="font-mono mr-1">{p.receipt_number}</span>}
                                  Acc: {p.account_number}
                                  {p.exchange_rate && ` | Rate: ${p.exchange_rate}`}
                                  {p.note && ` | ${p.note}`}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="text-right">
                                  <p className="font-bold text-green-600">
                                    <CurrencySymbol currency={p.loan_currency} />{fmtNum(p.amount)}
                                  </p>
                                  {p.debit_currency !== p.loan_currency && (
                                    <p className="text-xs text-slate-500">Debited: {p.debit_currency} {fmtNum(p.debit_amount)}</p>
                                  )}
                                </div>
                                <button onClick={(e) => { e.stopPropagation(); generatePaymentReceipt(p, loan); }}
                                  className="ml-2 p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                                  title="Download Receipt" data-testid={`receipt-${p.id}`}>
                                  <FileDown className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Write-Off History */}
                      {loanDetail.loan.write_offs && loanDetail.loan.write_offs.length > 0 && (
                        <div className="mt-4" data-testid={`write-off-history-${loan.id}`}>
                          <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
                            <XCircle className="w-4 h-4" /> Write-Off History
                          </h4>
                          <div className="space-y-2">
                            {loanDetail.loan.write_offs.map(wo => (
                              <div key={wo.id} className="flex justify-between items-center bg-amber-50 rounded p-3 border border-amber-200 text-sm"
                                data-testid={`write-off-entry-${wo.id}`}>
                                <div>
                                  <p className="font-medium text-amber-800">{new Date(wo.created_at).toLocaleDateString()} — Write-Off</p>
                                  <p className="text-xs text-amber-600">
                                    {wo.note && <span>{wo.note} · </span>}
                                    By {wo.written_off_by_name || wo.written_off_by}
                                  </p>
                                </div>
                                <p className="font-bold text-amber-700">
                                  -<CurrencySymbol currency={loan.currency} />{fmtNum(wo.amount)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HandCoins className="w-5 h-5" /> New {tab === 'loan' ? 'Loan' : 'Borrowing'}
              </DialogTitle>
              <DialogDescription className="sr-only">Create a new loan or borrowing entry</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-medium text-slate-500">Direction</Label>
                <div className="flex gap-2 mt-1">
                  <button onClick={() => setForm(f => ({ ...f, direction: 'to' }))}
                    className={`flex-1 p-2 border rounded text-sm text-center transition-colors ${form.direction === 'to' ? 'border-blue-500 bg-blue-50 font-semibold' : 'hover:border-blue-300'}`}>
                    Lent To (Receivable)
                  </button>
                  <button onClick={() => setForm(f => ({ ...f, direction: 'from' }))}
                    className={`flex-1 p-2 border rounded text-sm text-center transition-colors ${form.direction === 'from' ? 'border-purple-500 bg-purple-50 font-semibold' : 'hover:border-purple-300'}`}>
                    Received From (Payable)
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-slate-500">Person / Entity *</Label>
                  <Input value={form.person} onChange={e => setForm(f => ({ ...f, person: e.target.value }))} placeholder="Name" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Purpose</Label>
                  <Input value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} placeholder="e.g. Business expansion" className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-medium text-slate-500">Amount *</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Currency</Label>
                  <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                    {['USD', 'MVR', 'EUR', 'GBP', 'AED', 'SGD'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Date</Label>
                  <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-slate-500">Repayment Structure</Label>
                  <select value={form.repayment_structure} onChange={e => setForm(f => ({ ...f, repayment_structure: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                    <option value="full_payment">Full Payment</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Due Date</Label>
                  <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500">Repayment Condition</Label>
                <Input value={form.repayment_condition} onChange={e => setForm(f => ({ ...f, repayment_condition: e.target.value }))} placeholder="e.g. 5% interest, repay within 12 months" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500">Reminder (days before due)</Label>
                <select value={form.reminder_days} onChange={e => setForm(f => ({ ...f, reminder_days: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="reminder-days-select">
                  {[3, 5, 7, 14, 30].map(d => <option key={d} value={d}>{d} days</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500">
                  {tab === 'loan' ? 'Bank Account to Debit *' : 'Bank Account to Credit *'}
                </Label>
                <p className="text-xs text-slate-400 mb-1">
                  {tab === 'loan' ? 'Money will be deducted from this account' : 'Money will be added to this account'}
                </p>
                <div className="space-y-2 mt-1">
                  {banks.map(bank => (
                    <div key={bank.id}>
                      <p className="text-xs font-semibold text-slate-600 mb-1">{bank.bank_name}</p>
                      {bank.accounts.map((acc, idx) => (
                        <button key={`create-${bank.id}-${acc.account_number}-${idx}`}
                          onClick={() => setForm(f => ({ ...f, bank_id: bank.id, account_number: acc.account_number, exchange_rate: '' }))}
                          data-testid={`create-bank-${bank.id}-${acc.account_number}`}
                          className={`w-full flex justify-between items-center p-2 border rounded text-sm mb-1 transition-colors ${
                            form.bank_id === bank.id && form.account_number === acc.account_number
                              ? (tab === 'loan' ? 'border-blue-500 bg-blue-50 font-semibold' : 'border-purple-500 bg-purple-50 font-semibold')
                              : 'hover:border-slate-400'
                          }`}>
                          <span>{acc.account_number} ({acc.currency})</span>
                          <span className="font-mono text-xs"><CurrencySymbol currency={acc.currency} />{fmtNum(acc.balance || 0)}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                  {banks.length === 0 && (
                    <p className="text-xs text-slate-400 italic">No bank accounts configured. Add them in Accounting settings.</p>
                  )}
                </div>
              </div>
              {createNeedsExchangeRate && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Label className="text-xs font-medium text-amber-700">
                    Exchange Rate ({form.currency} to {selectedCreateAccount.currency}) *
                  </Label>
                  <p className="text-xs text-amber-600 mb-1">1 {form.currency} = ? {selectedCreateAccount.currency}</p>
                  <Input type="number" step="0.0001"
                    value={form.exchange_rate} onChange={e => setForm(f => ({ ...f, exchange_rate: e.target.value }))}
                    placeholder="e.g. 15.42" className="mt-1" data-testid="create-exchange-rate-input" />
                  {form.amount && form.exchange_rate && (
                    <p className="text-xs text-amber-700 mt-1 font-medium">
                      {tab === 'loan' ? 'Will debit' : 'Will credit'}: {selectedCreateAccount.currency} {fmtNum(parseFloat(form.amount || 0) * parseFloat(form.exchange_rate || 0))}
                    </p>
                  )}
                </div>
              )}
              <Button onClick={handleCreate} disabled={submitting || !form.bank_id || (createNeedsExchangeRate && !form.exchange_rate)} className="w-full" data-testid="submit-create-loan">
                {submitting ? 'Creating...' : `Create ${tab === 'loan' ? 'Loan' : 'Borrowing'}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Banknote className="w-5 h-5 text-emerald-600" /> Make Payment
              </DialogTitle>
              <DialogDescription className="sr-only">Process a payment for this loan or borrowing</DialogDescription>
            </DialogHeader>
            {selectedLoan && (
              <div className="space-y-3">
                <Card className="p-3 bg-slate-50">
                  <p className="text-sm font-medium">{selectedLoan.person} — {selectedLoan.type === 'loan' ? 'Loan' : 'Borrowing'}</p>
                  <p className="text-xs text-slate-500">
                    Remaining: <span className="font-bold text-amber-600"><CurrencySymbol currency={selectedLoan.currency} />{fmtNum(selectedLoan.remaining)}</span>
                  </p>
                </Card>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Payment Amount ({selectedLoan.currency}) *</Label>
                  <Input type="number" step="0.01" max={selectedLoan.remaining}
                    value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                    placeholder="0.00" className="mt-1" data-testid="payment-amount" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Bank Account to Debit *</Label>
                  <div className="space-y-2 mt-1">
                    {banks.map(bank => (
                      <div key={bank.id}>
                        <p className="text-xs font-semibold text-slate-600 mb-1">{bank.bank_name}</p>
                        {bank.accounts.map((acc, idx) => (
                          <button key={`${bank.id}-${acc.account_number}-${idx}`}
                            onClick={() => setPayForm(f => ({ ...f, bank_id: bank.id, account_number: acc.account_number, exchange_rate: '' }))}
                            className={`w-full flex justify-between items-center p-2 border rounded text-sm mb-1 transition-colors ${
                              payForm.bank_id === bank.id && payForm.account_number === acc.account_number
                                ? 'border-emerald-500 bg-emerald-50 font-semibold' : 'hover:border-emerald-300'
                            }`}>
                            <span>{acc.account_number} ({acc.currency})</span>
                            <span className="font-mono text-xs"><CurrencySymbol currency={acc.currency} />{fmtNum(acc.balance || 0)}</span>
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
                {needsExchangeRate && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Label className="text-xs font-medium text-amber-700">
                      Exchange Rate ({selectedLoan.currency} to {selectedPayAccount.currency}) *
                    </Label>
                    <p className="text-xs text-amber-600 mb-1">1 {selectedLoan.currency} = ? {selectedPayAccount.currency}</p>
                    <Input type="number" step="0.0001"
                      value={payForm.exchange_rate} onChange={e => setPayForm(f => ({ ...f, exchange_rate: e.target.value }))}
                      placeholder="e.g. 15.42" className="mt-1" data-testid="exchange-rate-input" />
                    {payForm.amount && payForm.exchange_rate && (
                      <p className="text-xs text-amber-700 mt-1 font-medium">
                        Will debit: {selectedPayAccount.currency} {fmtNum(parseFloat(payForm.amount || 0) * parseFloat(payForm.exchange_rate || 0))}
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <Label className="text-xs font-medium text-slate-500">Note (optional)</Label>
                  <Input value={payForm.note} onChange={e => setPayForm(f => ({ ...f, note: e.target.value }))} placeholder="Payment note" className="mt-1" />
                </div>
                <Button onClick={handlePayment} disabled={submitting || !payForm.amount || !payForm.bank_id || (needsExchangeRate && !payForm.exchange_rate)} className="w-full" data-testid="submit-payment">
                  {submitting ? 'Processing...' : 'Confirm Payment'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Write-Off Dialog */}
        <Dialog open={showWriteOffDialog} onOpenChange={setShowWriteOffDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-amber-600" /> Write Off
              </DialogTitle>
              <DialogDescription className="sr-only">Write off a portion of this loan as a loss</DialogDescription>
            </DialogHeader>
            {selectedLoan && (
              <div className="space-y-3">
                <Card className="p-3 bg-amber-50 border-amber-200">
                  <p className="text-sm font-medium">{selectedLoan.person} — {selectedLoan.type === 'loan' ? 'Loan' : 'Borrowing'}</p>
                  <p className="text-xs text-slate-500">
                    Remaining: <span className="font-bold text-amber-600"><CurrencySymbol currency={selectedLoan.currency} />{fmtNum(selectedLoan.remaining)}</span>
                  </p>
                </Card>
                <p className="text-xs text-slate-500">
                  This will reduce the outstanding balance and record the amount as a loss in accounting.
                </p>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Write-Off Amount ({selectedLoan.currency}) *</Label>
                  <Input type="number" step="0.01" max={selectedLoan.remaining}
                    value={writeOffForm.amount} onChange={e => setWriteOffForm(f => ({ ...f, amount: e.target.value }))}
                    placeholder="0.00" className="mt-1" data-testid="write-off-amount" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Reason / Note</Label>
                  <Input value={writeOffForm.note} onChange={e => setWriteOffForm(f => ({ ...f, note: e.target.value }))}
                    placeholder="e.g. Uncollectible debt" className="mt-1" data-testid="write-off-note" />
                </div>
                <Button onClick={handleWriteOff} disabled={submitting || !writeOffForm.amount}
                  className="w-full bg-amber-600 hover:bg-amber-700" data-testid="submit-write-off">
                  {submitting ? 'Processing...' : `Write Off ${selectedLoan.currency} ${writeOffForm.amount || '0.00'}`}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
